import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api, useAuth } from '../../App';
import { stripeErrorToast } from '../../utils/stripeErrors';
import { Calendar } from '../ui/calendar';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Clock, Calendar as CalendarIcon, Tag, ShieldCheck, MapPin, ClipboardList, Bell, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { formatDuration } from '../../utils/format';
import { getPolicy } from '../../utils/policies';
import { ServiceLocationPicker } from './ServiceLocationPicker';
import { BookingSummary } from './BookingSummary';
import { GuestCheckoutDialog } from './GuestCheckoutDialog';
import { GuestWaitlistDialog } from './GuestWaitlistDialog';
import { CardOnFileDialog } from './CardOnFileDialog';
import { NextAvailableSlots } from './NextAvailableSlots';
import { getNextAvailableSlots } from '../../utils/nextAvailableSlots';
import ShareButton from '../ShareButton';
import { getBookingSource, getReferralCode } from '../../utils/bookingSource';

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
];

const DAY_MAP = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function getBookButtonText(service, totalPrice) {
  if (service.payment_type === 'deposit_50') {
    return `Pay Deposit & Book, £${(totalPrice * 0.5).toFixed(2)}`;
  }
  if (service.payment_type === 'minimum_deposit') {
    const deposit = Math.min(service.deposit_amount || (totalPrice * 0.2), totalPrice);
    return `Pay Deposit & Book, £${deposit.toFixed(2)}`;
  }
  return `Pay & Book, £${totalPrice.toFixed(2)}`;
}

function formatNoticeperiod(hours) {
  if (hours >= 168) return '1 week';
  if (hours >= 24) {
    const days = Math.round(hours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
  }
  return `${hours} hour${hours > 1 ? 's' : ''}`;
}

export function BookingPanel({ service, user }) {
  const { betaConfig } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [onFullDayWaitlist, setOnFullDayWaitlist] = useState(false);
  const [fullDayWaitlistPending, setFullDayWaitlistPending] = useState(false);
  const [showGuestWaitlistDialog, setShowGuestWaitlistDialog] = useState(false);
  const [guestWaitlistMode, setGuestWaitlistMode] = useState('slot'); // 'slot' | 'full-day'
  const [pendingBooking, setPendingBooking] = useState(null); // {booking_id, max_fee, guest_token?}
  const [showCardDialog, setShowCardDialog] = useState(false);

  // Guest checkout state
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Add-ons state
  const [addons, setAddons] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);

  // Retail upsell state (Phase 1). `recommendedProducts` is the list
  // the provider has attached to this service; `selectedProductQtys`
  // is a map of product_id -> qty (0 means unselected). Items here
  // are NOT paid through Stripe today, they are settled in person at
  // the appointment, so the summary shows a clear split.
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [selectedProductQtys, setSelectedProductQtys] = useState({});

  // Availability state
  const [availability, setAvailability] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Waitlist counts per slot (Pro providers only), {time: count}
  const [waitlistCounts, setWaitlistCounts] = useState({});

  // Discount/promo state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Service location state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [travelQuote, setTravelQuote] = useState(null);
  const [clientPostcode, setClientPostcode] = useState(null);
  const [clientAddress, setClientAddress] = useState({ line1: '', line2: '', city: '', postcode: '' });
  const [addressQuoteLoading, setAddressQuoteLoading] = useState(false);

  // When the user types a postcode into the final "Your Address" form,
  // auto-fetch a travel quote so they see the out-of-range banner (and
  // can't hit Pay & Book) without having to wait for a server-side
  // reject on submit. Debounced 800ms so we don't spam geocoding on
  // every keystroke.
  useEffect(() => {
    if (selectedLocation !== 'mobile') return undefined;
    const raw = (clientAddress.postcode || '').trim();
    if (raw.length < 4 || !service?.provider_id) return undefined;
    // Skip if the upstream PostcodeLookup already fetched for this postcode.
    if (clientPostcode && clientPostcode.toUpperCase() === raw.toUpperCase() && travelQuote) {
      return undefined;
    }
    let cancelled = false;
    setAddressQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/providers/${service.provider_id}/travel-quote?postcode=${encodeURIComponent(raw)}`);
        if (cancelled) return;
        setTravelQuote(res.data);
        setClientPostcode(raw);
      } catch {
        if (cancelled) return;
        // On a bad postcode the server returns 400. Treat as "not in
        // range" so Pay & Book is blocked.
        setTravelQuote({ within_radius: false, breakdown: 'We could not validate that postcode.' });
      } finally {
        if (!cancelled) setAddressQuoteLoading(false);
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientAddress.postcode, selectedLocation, service?.provider_id, clientPostcode, travelQuote]);

  const fetchAddons = useCallback(async () => {
    if (!service?.provider_id) return;
    try {
      const res = await api.get(`/addons?provider_id=${service.provider_id}&service_id=${service.service_id}`);
      setAddons(res.data || []);
    } catch (err) { console.error('Addons fetch error:', err); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.provider_id, service?.service_id]);

  // Retail upsell, Phase 1. Pulls the recommended products attached to
  // this service. Public endpoint; failures are silent because the
  // upsell panel is optional and should never block the booking flow.
  const fetchRecommendedProducts = useCallback(async () => {
    if (!service?.service_id) return;
    try {
      const res = await api.get(`/services/${service.service_id}/products`);
      setRecommendedProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error('Recommended products fetch error:', err); }
  }, [service?.service_id]);

  useEffect(() => { if (service?.provider_id) fetchAddons(); }, [fetchAddons, service?.provider_id]);
  useEffect(() => { if (service?.service_id) fetchRecommendedProducts(); }, [fetchRecommendedProducts, service?.service_id]);

  const refreshAvailability = useCallback(async () => {
    if (!service?.provider_id) return null;
    try {
      const res = await api.get(`/providers/${service.provider_id}/calendar`);
      setAvailability(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [service?.provider_id]);

  useEffect(() => {
    if (service?.provider_id) {
      refreshAvailability();
    }
  }, [service?.provider_id, refreshAvailability]);

  // Fetch waitlist counts for the selected date (Pro providers only, social proof)
  useEffect(() => {
    if (!selectedDate || !service?.service_id || !service?.provider_id) {
      setWaitlistCounts({});
      return;
    }
    if (service?.provider?.subscription_tier !== 'pro') {
      setWaitlistCounts({});
      return;
    }
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    api.get(`/waitlist/counts?service_id=${service.service_id}&provider_id=${service.provider_id}&date=${dateStr}`)
      .then(res => setWaitlistCounts(res.data?.counts || {}))
      .catch(() => setWaitlistCounts({}));
  }, [selectedDate, service?.service_id, service?.provider_id, service?.provider?.subscription_tier]);

  const getPeakSurcharge = () => {
    if (!selectedDate || !availability?.peak_pricing?.enabled) return 0;
    const dayName = DAY_MAP[selectedDate.getDay()];
    if (!availability.peak_pricing.days.includes(dayName)) return 0;
    if (availability.peak_pricing.type === 'percentage') {
      return Math.round(service.price * (availability.peak_pricing.amount / 100) * 100) / 100;
    }
    return availability.peak_pricing.amount || 0;
  };

  const peakSurcharge = getPeakSurcharge();

  // Calculate travel fee based on provider's travel charge settings
  const getTravelFee = () => {
    if (selectedLocation !== 'mobile') return 0;
    const provider = service.provider;
    if (!provider) return 0;
    if (provider.free_travel) return 0;

    // Use exact quote if available from postcode lookup
    if (travelQuote && travelQuote.within_radius && travelQuote.fee !== null) {
      return Number(travelQuote.fee) || 0;
    }

    // Fallback to highest tier if no postcode entered
    const charges = provider.travel_charges || [];
    if (charges.length === 0) return 0;
    const sorted = [...charges].sort((a, b) => a.max_miles - b.max_miles);
    return sorted.length > 0 ? Number(sorted[sorted.length - 1].charge) || 0 : 0;
  };

  const travelFee = getTravelFee();

  const handleTravelQuote = (result, postcode) => {
    setTravelQuote(result);
    setClientPostcode(postcode);
  };

  // Reset travel quote when location changes away from mobile
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    if (location !== 'mobile') {
      setTravelQuote(null);
      setClientPostcode(null);
    }
  };

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return false;
    const minNotice = availability.min_notice_hours || 0;
    if (minNotice > 0) {
      const now = new Date();
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59);
      if ((endOfDay - now) / (1000 * 60 * 60) < minNotice) return true;
    }
    const dayName = DAY_MAP[date.getDay()];
    const schedule = availability.weekly_schedule?.[dayName];
    if (schedule && !schedule.enabled) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (availability.blocked_dates?.find(b => b.date === dateStr && b.all_day)) return true;
    // Fully booked: every generable slot for this date is in booked_slots.
    // For Pro providers we leave the date selectable so the client can
    // one-click join the waitlist for any opening. For non-Pro providers
    // there's no waitlist to join, so we disable as before.
    const slots = computeSlotsForDate(date);
    if (slots.length > 0 && slots.every(s => s.status === 'booked')) {
      if (service?.provider?.subscription_tier !== 'pro') return true;
    }
    return false;
  };

  // Shared slot generator, used by both isDateDisabled and the Time Selection grid.
  // A slot [T, T+service.duration) is 'booked' if it overlaps ANY existing booking's
  // [B.start, B.start+B.duration) range. Exact start-time match is NOT enough,
  // a 2-hour booking at 13:30 must block 14:00 and 14:30 too.
  const computeSlotsForDate = (date) => {
    if (!date || !availability) return [];
    const dayName = DAY_MAP[date.getDay()];
    const schedule = availability.weekly_schedule?.[dayName];
    if (!schedule || !schedule.enabled) return [];
    const startMinutes = parseInt(schedule.start.split(':')[0]) * 60 + parseInt(schedule.start.split(':')[1]);
    const endMinutes = parseInt(schedule.end.split(':')[0]) * 60 + parseInt(schedule.end.split(':')[1]);
    const dateStr = format(date, 'yyyy-MM-dd');
    const blockedRanges = (availability.blocked_dates || [])
      .filter(b => b.date === dateStr && !b.all_day && b.start_time && b.end_time)
      .map(b => ({
        start: parseInt(b.start_time.split(':')[0]) * 60 + parseInt(b.start_time.split(':')[1]),
        end: parseInt(b.end_time.split(':')[0]) * 60 + parseInt(b.end_time.split(':')[1]),
      }));
    const bookedRanges = (availability.booked_slots || [])
      .filter(b => b.date === dateStr)
      .map(b => {
        const [bh, bm] = b.time.split(':').map(Number);
        const startMin = bh * 60 + bm;
        return { start: startMin, end: startMin + (Number(b.duration) || 60) };
      });
    const newDuration = Number(service?.duration) || 60;
    const minNotice = availability.min_notice_hours || 0;
    const now = new Date();
    const out = [];
    for (const slot of TIME_SLOTS) {
      const [h, m] = slot.split(':').map(Number);
      const mins = h * 60 + m;
      if (mins < startMinutes || mins >= endMinutes) continue;
      // The service must finish within the provider's day, no half-overlapping end.
      const slotEnd = mins + newDuration;
      if (slotEnd > endMinutes) continue;
      let inBlocked = false;
      for (const range of blockedRanges) {
        if (mins < range.end && slotEnd > range.start) { inBlocked = true; break; }
      }
      if (inBlocked) continue;
      if (minNotice > 0) {
        const slotDate = new Date(date);
        slotDate.setHours(h, m, 0, 0);
        if ((slotDate - now) / (1000 * 60 * 60) < minNotice) continue;
      }
      // Range-overlap check against each existing booking.
      let booked = false;
      for (const range of bookedRanges) {
        if (mins < range.end && slotEnd > range.start) { booked = true; break; }
      }
      out.push({ time: slot, status: booked ? 'booked' : 'available' });
    }
    return out;
  };

  const getTimeSlotsForDate = () => {
    if (!selectedDate) return TIME_SLOTS.map(t => ({ time: t, status: 'available' }));
    return computeSlotsForDate(selectedDate);
  };

  const allTimeSlots = getTimeSlotsForDate();
  const availableTimeSlots = allTimeSlots.filter(s => s.status === 'available').map(s => s.time);
  const selectedSlotStatus = allTimeSlots.find(s => s.time === selectedTime)?.status;
  // Pro-tier features (waitlist, priority slots) are enabled during
  // the Founding Provider Beta for all approved providers.
  const providerApproved = service?.provider?.status === 'approved';
  const isProProvider = betaConfig?.beta_mode
    ? providerApproved
    : service?.provider?.subscription_tier === 'pro';
  const isDateFullyBooked = selectedDate
    && allTimeSlots.length > 0
    && allTimeSlots.every(s => s.status === 'booked');

  const totalPriceBeforeDiscount = service.price + peakSurcharge + travelFee + addons.filter(a => selectedAddonIds.includes(a.addon_id)).reduce((sum, a) => sum + a.price, 0);

  const totalPrice = appliedDiscount
    ? Math.max(totalPriceBeforeDiscount - appliedDiscount.discount_amount, 0)
    : totalPriceBeforeDiscount;

  // Next-available suggestions when the picked slot is taken or the whole
  // day is booked. Keeps the client out of the dead-end and visibly pulls
  // 2-3 tap-to-fill options from the same /calendar state the grid uses,
  // so the suggestions stay consistent with what's rendered on screen.
  const shouldShowNextSlots = Boolean(
    selectedDate && (selectedSlotStatus === 'booked' || isDateFullyBooked)
  );

  const nextSlotSuggestions = useMemo(() => {
    if (!shouldShowNextSlots || !availability) return [];
    return getNextAvailableSlots({
      availability,
      computeSlotsForDate,
      isDateDisabled,
      fromDate: selectedDate,
      fromTime: selectedSlotStatus === 'booked' ? selectedTime : null,
      count: 3,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowNextSlots, availability, selectedDate, selectedTime, selectedSlotStatus]);

  const handlePickSuggestion = (suggestion) => {
    setSelectedDate(suggestion.date);
    setSelectedTime(suggestion.time);
    setOnWaitlist(false);
    setOnFullDayWaitlist(false);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const res = await api.post('/discounts/validate', {
        code: promoCode.trim().toUpperCase(),
        provider_id: service.provider_id,
        amount: totalPriceBeforeDiscount,
        client_id: user?.user_id || null,
      });
      setAppliedDiscount(res.data);
      toast.success(`Discount applied: ${res.data.discount_type === 'fixed' ? '£' + res.data.discount_amount : res.data.value + '% off'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setAppliedDiscount(null);
    } finally { setPromoValidating(false); }
  };

  const removePromo = () => { setAppliedDiscount(null); setPromoCode(''); };

  const toggleAddon = (addonId) => {
    setSelectedAddonIds(prev => prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]);
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) { toast.error('Please select a date and time'); return; }
    const providerTypes = service.provider?.service_types || [];
    if (providerTypes.length > 1 && !selectedLocation) { toast.error('Please select where you\'d like your service'); return; }
    if (!user) { setShowGuestDialog(true); return; }
    await processBooking();
  };

  const handleGuestCheckout = async () => {
    if (!guestName || !guestEmail) { toast.error('Please enter your name and email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) { toast.error('Please enter a valid email address'); return; }
    if (selectedLocation === 'mobile' && !clientAddress.line1) { toast.error('Please enter your address for mobile bookings'); return; }
    setBookingLoading(true);
    try {
      const bookingSource = getBookingSource(service.provider?.username);
      const referralCode = getReferralCode();
      const booking = await api.post('/bookings/guest', {
        service_id: service.service_id, provider_id: service.provider_id,
        date: format(selectedDate, 'yyyy-MM-dd'), time: selectedTime, notes,
        guest_name: guestName, guest_email: guestEmail, guest_phone: guestPhone,
        addon_ids: selectedAddonIds, discount_code: appliedDiscount ? appliedDiscount.code : null,
        service_location: selectedLocation,
        client_postcode: clientPostcode || clientAddress.postcode || null,
        client_address: selectedLocation === 'mobile' ? clientAddress : null,
        booking_source: bookingSource,
        referral_code: referralCode,
        requested_upsell_products: Object.entries(selectedProductQtys)
          .filter(([, qty]) => qty > 0)
          .map(([product_id, qty]) => ({ product_id, qty })),
      });
      if (booking.data.card_on_file_required) {
        setPendingBooking({
          booking_id: booking.data.booking_id,
          max_fee: booking.data.card_on_file_max_fee || 0,
          guest_token: booking.data.guest_token,
        });
        setShowCardDialog(true);
        setShowGuestDialog(false);
        setBookingLoading(false);
        return;
      }
      const payment = await api.post(`/payments/checkout/guest?booking_id=${booking.data.booking_id}&email=${encodeURIComponent(guestEmail)}`);
      window.location.href = payment.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally { setBookingLoading(false); }
  };

  const processBooking = async () => {
    // Validate client address for mobile bookings
    if (selectedLocation === 'mobile' && !clientAddress.line1) {
      toast.error('Please enter your address for mobile bookings');
      return;
    }
    setBookingLoading(true);
    try {
      const bookingSource = getBookingSource(service.provider?.username);
      const referralCode = getReferralCode();
      // Smart Waitlist priority offer: if the client arrived via an
      // offer link (?waitlist_claim=entry_id), forward the entry_id so
      // the backend can close the queue and mark the entry as booked.
      const waitlistEntryId = new URLSearchParams(window.location.search).get('waitlist_claim');
      const booking = await api.post('/bookings', {
        service_id: service.service_id, provider_id: service.provider_id,
        date: format(selectedDate, 'yyyy-MM-dd'), time: selectedTime, notes,
        addon_ids: selectedAddonIds, discount_code: appliedDiscount ? appliedDiscount.code : null,
        service_location: selectedLocation,
        client_postcode: clientPostcode || clientAddress.postcode || null,
        client_address: selectedLocation === 'mobile' ? clientAddress : null,
        booking_source: bookingSource,
        referral_code: referralCode,
        requested_upsell_products: Object.entries(selectedProductQtys)
          .filter(([, qty]) => qty > 0)
          .map(([product_id, qty]) => ({ product_id, qty })),
        waitlist_entry_id: waitlistEntryId || undefined,
      });
      // Card-on-file gate: collect card before proceeding to payment
      if (booking.data.card_on_file_required) {
        setPendingBooking({
          booking_id: booking.data.booking_id,
          max_fee: booking.data.card_on_file_max_fee || 0,
        });
        setShowCardDialog(true);
        setBookingLoading(false);
        return;
      }
      const payment = await api.post(`/payments/checkout?booking_id=${booking.data.booking_id}`);
      window.location.href = payment.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally { setBookingLoading(false); }
  };

  const continueToCheckoutAfterCard = async () => {
    if (!pendingBooking) return;
    try {
      const payment = await api.post(`/payments/checkout?booking_id=${pendingBooking.booking_id}`);
      window.location.href = payment.data.url;
    } catch (error) {
      toast.error(stripeErrorToast(error, 'Checkout failed'));
    }
  };

  const continueToGuestCheckoutAfterCard = async () => {
    if (!pendingBooking) return;
    try {
      const payment = await api.post(
        `/payments/checkout/guest?booking_id=${pendingBooking.booking_id}&email=${encodeURIComponent(guestEmail)}`
      );
      window.location.href = payment.data.url;
    } catch (error) {
      toast.error(stripeErrorToast(error, 'Checkout failed'));
    }
  };

  const handleJoinWaitlist = async () => {
    if (!selectedDate || !selectedTime) { toast.error('Select a date and time'); return; }
    // Guest users, collect their details first
    if (!user) {
      setGuestWaitlistMode('slot');
      setShowGuestWaitlistDialog(true);
      return;
    }
    await submitWaitlist({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      isGuest: false,
    });
  };

  const submitWaitlist = async ({ name, email, phone, isGuest }) => {
    // Full-day fan-out, join every booked slot on the selected date in one tap.
    if (guestWaitlistMode === 'full-day') {
      return submitFullDayWaitlist({ name, email, phone, isGuest });
    }
    setWaitlistLoading(true);
    try {
      await api.post('/waitlist/join', {
        service_id: service.service_id,
        provider_id: service.provider_id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        client_name: name,
        client_email: email,
        client_phone: phone,
        is_guest: isGuest,
      });
      setOnWaitlist(true);
      setShowGuestWaitlistDialog(false);
      setWaitlistCounts(prev => ({ ...prev, [selectedTime]: (prev[selectedTime] || 0) + 1 }));
      toast.success('You\'re on the waitlist! We\'ll notify you if this slot opens up.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally { setWaitlistLoading(false); }
  };

  const submitFullDayWaitlist = async ({ name, email, phone, isGuest }) => {
    if (!selectedDate) return;
    const bookedTimes = allTimeSlots.filter(s => s.status === 'booked').map(s => s.time);
    if (bookedTimes.length === 0) return;
    setWaitlistLoading(true);
    setFullDayWaitlistPending(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const results = await Promise.allSettled(
        bookedTimes.map(t => api.post('/waitlist/join', {
          service_id: service.service_id,
          provider_id: service.provider_id,
          date: dateStr,
          time: t,
          client_name: name,
          client_email: email,
          client_phone: phone,
          is_guest: isGuest,
        }))
      );
      const added = results.filter(r => r.status === 'fulfilled').length;
      const alreadyOn = results.filter(r =>
        r.status === 'rejected' &&
        /already on the waitlist/i.test(r.reason?.response?.data?.detail || '')
      ).length;
      // Any slot that succeeded OR was already listed counts as "on the waitlist".
      if (added + alreadyOn > 0) {
        setOnFullDayWaitlist(true);
        setShowGuestWaitlistDialog(false);
        setWaitlistCounts(prev => {
          const next = { ...prev };
          bookedTimes.forEach(t => { next[t] = (next[t] || 0) + 1; });
          return next;
        });
        toast.success(
          added > 0
            ? `You\'re on the waitlist for every slot on ${format(selectedDate, 'EEE d MMM')}. We\'ll text you the moment one opens up.`
            : 'You\'re already on the waitlist for every slot on this date.'
        );
      } else {
        const firstErr = results.find(r => r.status === 'rejected');
        toast.error(firstErr?.reason?.response?.data?.detail || 'Failed to join waitlist');
      }
    } finally {
      setWaitlistLoading(false);
      setFullDayWaitlistPending(false);
    }
  };

  const handleJoinFullDayWaitlist = async () => {
    if (!selectedDate) return;
    if (!user) {
      setGuestWaitlistMode('full-day');
      setShowGuestWaitlistDialog(true);
      return;
    }
    await submitFullDayWaitlist({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      isGuest: false,
    });
  };


  return (
    <>
      <div className="lg:sticky lg:top-24 h-fit">
        <div className="card-surface p-6">
          <span className="overline mb-2 block">{service.category}</span>
          <div className="flex items-start justify-between gap-3 mb-2">
            <h1 className="font-heading text-3xl font-medium text-[#1A1A1A]" data-testid="service-title">
              {service.name}
            </h1>
            <ShareButton
              path={`/service/${service.service_id}`}
              title={`${service.name} - ${service.provider?.brand_name || 'Zuriya'}`}
              description={`£${service.price} · ${formatDuration(service.duration)}`}
            />
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <span className="text-3xl font-semibold text-[#D4AF37]">£{service.price}</span>
            <span className="flex items-center gap-1 text-stone-500">
              <Clock className="h-4 w-4" />
              {formatDuration(service.duration)}
            </span>
            <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="no-booking-fee-label">
              No booking fee
            </span>
          </div>

          <p className="text-stone-600 mb-6">{service.description}</p>

          {/* Deposit & Policy Info */}
          {(service.deposit_amount || service.cancellation_policy) && (
            <div className="mb-4 space-y-2" data-testid="service-policies">
              {service.deposit_amount && (
                <div className="flex items-center gap-2.5 p-3 bg-[#F9F6F0] border border-[#E6E4DD] rounded-lg">
                  <span className="text-sm text-[#1A1A1A]">
                    <span className="font-semibold text-[#D4AF37]">£{service.deposit_amount.toFixed(2)}</span> deposit required at booking
                    <span className="text-stone-500"> · remaining £{(service.price - service.deposit_amount).toFixed(2)} due at appointment</span>
                  </span>
                </div>
              )}
              {service.cancellation_policy && (
                <div className="flex items-start gap-2.5 p-3 bg-stone-50 border border-[#E6E4DD] rounded-lg" data-testid="service-cancellation-policy">
                  <ShieldCheck className="h-4 w-4 text-stone-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{getPolicy(service.cancellation_policy.type).label}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{getPolicy(service.cancellation_policy.type).summary}</p>
                  </div>
                </div>
              )}

              {/* Pre-Appointment Rules */}
              {service.provider?.pre_appointment_rules?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg" data-testid="pre-appointment-rules">
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" />
                    Before your appointment
                  </p>
                  <ul className="space-y-1">
                    {service.provider.pre_appointment_rules.map((rule, i) => (
                      <li key={`rule-${i}-${rule.slice(0, 40)}`} className="flex items-start gap-2 text-sm text-amber-900">
                        <span className="text-amber-500 mt-0.5 shrink-0">&#10003;</span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Notice Period Info */}
          {availability?.min_notice_hours > 0 && (
            <div className="mb-4 flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="notice-period-info">
              <CalendarIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                Please book at least{' '}
                <span className="font-semibold">
                  {formatNoticeperiod(availability.min_notice_hours)}
                </span>{' '}
                in advance
              </p>
            </div>
          )}

          {/* Service Location Selection */}
          {service.provider?.service_types && service.provider.service_types.length > 0 && (
            <ServiceLocationPicker
              serviceTypes={service.provider.service_types}
              selected={selectedLocation}
              onSelect={handleLocationSelect}
              provider={service.provider}
              onTravelQuote={handleTravelQuote}
            />
          )}

          {/* Client Address for Mobile Bookings */}
          {selectedLocation === 'mobile' && (
            <div className="mb-6 p-4 bg-[#F9F6F0] rounded-lg space-y-3" data-testid="client-address-section">
              <h3 className="font-medium text-[#1A1A1A] text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#D4AF37]" />
                Your Address *
              </h3>
              <p className="text-xs text-stone-500">Required so the provider knows where to come.</p>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-stone-600">House/Flat Number & Street *</label>
                  <Input value={clientAddress.line1} onChange={e => setClientAddress(prev => ({ ...prev, line1: e.target.value }))} placeholder="e.g. 15 Atlantic Road" className="input-zuriya" data-testid="client-address-line1" />
                </div>
                <div>
                  <label className="text-xs text-stone-600">Flat/Unit (optional)</label>
                  <Input value={clientAddress.line2} onChange={e => setClientAddress(prev => ({ ...prev, line2: e.target.value }))} placeholder="e.g. Flat 2B" className="input-zuriya" data-testid="client-address-line2" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-stone-600">City *</label>
                    <Input value={clientAddress.city} onChange={e => setClientAddress(prev => ({ ...prev, city: e.target.value }))} placeholder="e.g. your city" className="input-zuriya" data-testid="client-address-city" />
                  </div>
                  <div>
                    <label className="text-xs text-stone-600">Postcode *</label>
                    <Input value={clientAddress.postcode} onChange={e => setClientAddress(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))} placeholder="e.g. SW9 8HX" className="input-zuriya" data-testid="client-address-postcode" />
                  </div>
                </div>
              </div>
              {addressQuoteLoading && (
                <p className="text-xs text-stone-400 flex items-center gap-1.5" data-testid="address-quote-loading">
                  <span className="inline-block w-3 h-3 border-2 border-stone-300 border-t-[#D4AF37] rounded-full animate-spin" />
                  Checking travel range...
                </p>
              )}
              {travelQuote && !travelQuote.within_radius && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="address-out-of-range">
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-amber-800">
                      {travelQuote.distance_miles
                        ? `${travelQuote.distance_miles} miles, outside travel range`
                        : 'Outside travel range'}
                    </p>
                    <p className="text-xs text-amber-600">
                      {travelQuote.breakdown || 'This provider does not travel to your postcode.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date Selection */}
          <div className="mb-6">
            <h3 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-[#D4AF37]" />
              Select Date
            </h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => { setSelectedDate(date); setSelectedTime(null); setOnWaitlist(false); setOnFullDayWaitlist(false); }}
              disabled={isDateDisabled}
              modifiers={{
                fullyBooked: (date) => {
                  if (!availability) return false;
                  if (date < new Date(new Date().setHours(0, 0, 0, 0))) return false;
                  const dayName = DAY_MAP[date.getDay()];
                  const sched = availability.weekly_schedule?.[dayName];
                  if (!sched || !sched.enabled) return false;
                  const slots = computeSlotsForDate(date);
                  // Only mark fully-booked on Pro providers, where the user can
                  // one-click join the waitlist. Non-Pro fully-booked days stay
                  // plainly disabled.
                  if (service?.provider?.subscription_tier !== 'pro') return false;
                  return slots.length > 0 && slots.every(s => s.status === 'booked');
                },
              }}
              modifiersClassNames={{
                fullyBooked: 'day-fully-booked',
              }}
              className="rounded-lg border border-[#E6E4DD]"
              data-testid="booking-calendar"
            />
          </div>

          {/* Time Selection */}
          {selectedDate && (
            <div className="mb-6 animate-fadeIn">
              <h3 className="font-medium text-[#1A1A1A] mb-3">Select Time</h3>
              {allTimeSlots.length === 0 ? (
                <p className="text-sm text-stone-500 bg-[#F9F6F0] p-3 rounded-lg">No available time slots for this date.</p>
              ) : (
                <>
                  {/* Fully-booked day prompt, one-click "Auto-add me to waitlist" */}
                  {isProProvider && isDateFullyBooked && (
                    <div
                      className="mb-3 p-4 rounded-lg bg-[#FFF8E7] border border-[#D4AF37]/50"
                      data-testid="waitlist-fully-booked-prompt"
                    >
                      <div className="flex items-start gap-2.5 mb-3">
                        <Bell className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
                        <div className="text-[13px] text-[#1A1A1A] leading-relaxed">
                          <p className="mb-1"><strong>{format(selectedDate, 'EEEE d MMMM')} is fully booked.</strong></p>
                          <p className="text-stone-600">Want to be on the waitlist in case someone reschedules? We&apos;ll text you the instant any slot opens up.</p>
                        </div>
                      </div>
                      <Button
                        onClick={handleJoinFullDayWaitlist}
                        disabled={waitlistLoading || fullDayWaitlistPending || onFullDayWaitlist}
                        className="w-full btn-primary h-11 text-sm rounded-lg gap-2"
                        data-testid="join-full-day-waitlist-btn"
                      >
                        <Bell className="h-4 w-4" />
                        {onFullDayWaitlist
                          ? 'You\'re on the waitlist for every slot'
                          : fullDayWaitlistPending
                            ? 'Joining...'
                            : 'Auto-add me to waitlist for any opening'}
                      </Button>
                      <p className="text-[11px] text-stone-500 mt-2 text-center">
                        Or tap any specific time below to join just that slot&apos;s waitlist.
                      </p>
                    </div>
                  )}
                  {/* Waitlist nudge, Pro providers with at least 1 booked slot on this date */}
                  {isProProvider && allTimeSlots.some(s => s.status === 'booked') && !isDateFullyBooked && (
                    <div
                      className="mb-3 p-3 rounded-lg bg-[#FFF8E7] border border-[#D4AF37]/40 flex items-start gap-2.5"
                      data-testid="waitlist-date-nudge"
                    >
                      <Bell className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div className="text-[13px] text-[#1A1A1A] leading-relaxed">
                        <p><strong>This date is in high demand.</strong> {allTimeSlots.filter(s => s.status === 'booked').length} slot{allTimeSlots.filter(s => s.status === 'booked').length === 1 ? '' : 's'} already booked, tap any crossed-out time to join its waitlist, and we'll notify you the moment it opens up.</p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {allTimeSlots.map(({ time, status }) => {
                    const isBooked = status === 'booked';
                    const count = waitlistCounts[time] || 0;
                    const showWaitingBadge = isProProvider && isBooked && count > 0;
                    // Only render booked slots if Pro (gives social proof), otherwise hide to avoid confusion
                    if (isBooked && !isProProvider) return null;
                    return (
                      <button
                        key={time}
                        onClick={() => {
                          setSelectedTime(time);
                          setOnWaitlist(false);
                          // Booked taps almost always want a nearby alternative,
                          // refresh so the suggestions reflect live state.
                          if (isBooked) {
                            setSuggestionsLoading(true);
                            refreshAvailability().finally(() => setSuggestionsLoading(false));
                          }
                        }}
                        className={`relative py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          selectedTime === time
                            ? isBooked
                              ? 'bg-stone-700 text-white ring-2 ring-[#D4AF37]'
                              : 'bg-[#D4AF37] text-white'
                            : isBooked
                              ? 'bg-stone-100 text-stone-500 border border-dashed border-stone-300 hover:bg-stone-200'
                              : 'bg-[#F9F6F0] text-[#1A1A1A] hover:bg-[#EAE4D9]'
                        }`}
                        data-testid={`time-slot-${time}`}
                      >
                        <span className={isBooked ? 'line-through opacity-70' : ''}>{time}</span>
                        {isBooked && (
                          <span className="block text-[10px] font-normal mt-0.5 leading-none">
                            {showWaitingBadge ? `${count} waiting` : 'Taken'}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  </div>
                </>
              )}
              {isProProvider && selectedSlotStatus === 'booked' && (
                <p className="text-xs text-stone-500 mt-3 bg-[#F9F6F0] p-2 rounded-lg" data-testid="slot-taken-hint">
                  This slot is taken. Join the waitlist below, we'll text you if it opens up.
                </p>
              )}
              {shouldShowNextSlots && (
                <NextAvailableSlots
                  suggestions={nextSlotSuggestions}
                  onPick={handlePickSuggestion}
                  loading={suggestionsLoading}
                  testIdPrefix="next-slot"
                />
              )}
            </div>
          )}

          {/* Notes */}
          {selectedTime && selectedSlotStatus !== 'booked' && (
            <div className="mb-6 animate-fadeIn">
              <h3 className="font-medium text-[#1A1A1A] mb-3">Additional Notes (Optional)</h3>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requests or information..."
                className="input-zuriya min-h-[100px]"
                data-testid="booking-notes"
              />
            </div>
          )}

          {/* Add-ons */}
          {addons.length > 0 && selectedTime && selectedSlotStatus !== 'booked' && (
            <div className="mb-6 animate-fadeIn" data-testid="addons-selection">
              <h3 className="font-medium text-[#1A1A1A] mb-3">Add-ons (Optional)</h3>
              <div className="space-y-2">
                {addons.map(addon => (
                  <label
                    key={addon.addon_id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedAddonIds.includes(addon.addon_id)
                        ? 'border-[#D4AF37] bg-[#F9F6F0]'
                        : 'border-[#E6E4DD] hover:border-[#D4AF37]/50'
                    }`}
                    data-testid={`addon-option-${addon.addon_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedAddonIds.includes(addon.addon_id)}
                        onChange={() => toggleAddon(addon.addon_id)}
                        className="w-4 h-4 rounded border-stone-300 text-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                      <span className="text-sm text-[#1A1A1A]">{addon.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#D4AF37]">+£{addon.price.toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Phase 1 retail upsell, non-modal "Recommended by your
              stylist" panel. Settled in person at the appointment for
              Phase 1; Phase 2 will fold this into a Stripe split-line
              checkout. */}
          {recommendedProducts.length > 0 && selectedTime && selectedSlotStatus !== 'booked' && (
            <div className="mb-6 animate-fadeIn" data-testid="upsell-products">
              <h3 className="font-medium text-[#1A1A1A] mb-1">Recommended by your stylist</h3>
              <p className="text-xs text-stone-500 mb-3">
                Tick what you want. Paid with your booking deposit, delivered at the appointment.
              </p>
              <div className="space-y-2">
                {recommendedProducts.map(p => {
                  const qty = selectedProductQtys[p.product_id] || 0;
                  const selected = qty > 0;
                  const outOfStock = (p.stock_qty || 0) <= 0;
                  return (
                    <div
                      key={p.product_id}
                      className={`p-3 rounded-lg border transition-colors ${
                        selected
                          ? 'border-[#D4AF37] bg-[#F9F6F0]'
                          : 'border-[#E6E4DD]'
                      } ${outOfStock ? 'opacity-50' : ''}`}
                      data-testid={`upsell-product-${p.product_id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt={p.name}
                              className="w-12 h-12 object-cover rounded border border-stone-200 shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded bg-stone-100 border border-stone-200 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A1A1A] truncate">{p.name}</p>
                            {p.description && (
                              <p className="text-xs text-stone-500 truncate">{p.description}</p>
                            )}
                            <p className="text-xs text-[#D4AF37] font-semibold mt-0.5">
                              £{Number(p.price).toFixed(2)}
                              {outOfStock && <span className="ml-2 text-stone-400">Out of stock</span>}
                            </p>
                          </div>
                        </div>
                        {!outOfStock && (
                          <div className="shrink-0">
                            {selected ? (
                              <div className="inline-flex items-center gap-2 border border-[#D4AF37] rounded-full bg-white">
                                <button
                                  type="button"
                                  className="w-7 h-7 flex items-center justify-center text-[#1A1A1A] hover:bg-stone-50 rounded-l-full"
                                  onClick={() => setSelectedProductQtys(q => ({
                                    ...q, [p.product_id]: Math.max(0, (q[p.product_id] || 0) - 1),
                                  }))}
                                  data-testid={`upsell-qty-down-${p.product_id}`}
                                  aria-label="Decrease quantity"
                                >
                                  -
                                </button>
                                <span className="text-sm font-mono w-5 text-center" data-testid={`upsell-qty-${p.product_id}`}>
                                  {qty}
                                </span>
                                <button
                                  type="button"
                                  className="w-7 h-7 flex items-center justify-center text-[#1A1A1A] hover:bg-stone-50 rounded-r-full disabled:opacity-40"
                                  onClick={() => setSelectedProductQtys(q => ({
                                    ...q, [p.product_id]: Math.min(p.stock_qty || 99, (q[p.product_id] || 0) + 1),
                                  }))}
                                  disabled={qty >= (p.stock_qty || 99)}
                                  data-testid={`upsell-qty-up-${p.product_id}`}
                                  aria-label="Increase quantity"
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="text-xs font-medium px-3 py-1.5 border border-[#1A1A1A] rounded-full text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors"
                                onClick={() => setSelectedProductQtys(q => ({ ...q, [p.product_id]: 1 }))}
                                data-testid={`upsell-add-${p.product_id}`}
                              >
                                Add
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Split summary: shows what the client pays now vs at
                  appointment, mirrors the cancellation/deposit pattern
                  already used on this page so the split is not jarring. */}
              {(() => {
                const upsellTotal = recommendedProducts.reduce((sum, p) => {
                  const q = selectedProductQtys[p.product_id] || 0;
                  return sum + q * Number(p.price);
                }, 0);
                if (upsellTotal <= 0) return null;
                return (
                  <div
                    className="mt-3 p-3 rounded-lg bg-stone-50 border border-stone-200 text-xs text-stone-700"
                    data-testid="upsell-summary"
                  >
                    <p className="flex items-center justify-between">
                      <span>Add-ons total</span>
                      <span className="font-semibold text-[#1A1A1A]">£{upsellTotal.toFixed(2)}</span>
                    </p>
                    <p className="mt-1 text-stone-500">
                      Charged with your booking deposit. Picked up or applied at the appointment.
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Promo Code */}
          {selectedDate && selectedTime && selectedSlotStatus !== 'booked' && (
            <div className="mb-6 animate-fadeIn" data-testid="promo-section">
              <h3 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4 text-[#D4AF37]" />
                Promo Code
              </h3>
              {appliedDiscount ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <span className="text-sm font-mono font-semibold text-green-700">{appliedDiscount.code}</span>
                    <span className="text-xs text-green-600 ml-2">
                      {appliedDiscount.discount_type === 'fixed' ? `£${appliedDiscount.discount_amount} off` : `${appliedDiscount.value}% off`}
                    </span>
                  </div>
                  <button onClick={removePromo} className="text-xs text-red-500 hover:underline" data-testid="remove-promo-btn">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="input-zuriya font-mono uppercase"
                    data-testid="promo-code-input"
                  />
                  <Button
                    variant="outline"
                    onClick={validatePromoCode}
                    disabled={!promoCode.trim() || promoValidating}
                    className="shrink-0"
                    data-testid="apply-promo-btn"
                  >
                    {promoValidating ? '...' : 'Apply'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Booking Summary, hide for taken slots */}
          {selectedSlotStatus !== 'booked' && (
            <BookingSummary
              service={service}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedLocation={selectedLocation}
              peakSurcharge={peakSurcharge}
              travelFee={travelFee}
              travelQuote={travelQuote}
              addons={addons}
              selectedAddonIds={selectedAddonIds}
              appliedDiscount={appliedDiscount}
              totalPriceBeforeDiscount={totalPriceBeforeDiscount}
              totalPrice={totalPrice}
            />
          )}

          {selectedSlotStatus !== 'booked' && (
            <>
              {service.provider?.is_tutorial && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2" data-testid="tutorial-booking-notice">
                  Tutorial profile, bookings disabled on this demo account.
                </p>
              )}
              <Button
                onClick={handleBooking}
                disabled={
                  !selectedDate ||
                  !selectedTime ||
                  bookingLoading ||
                  service.provider?.is_tutorial ||
                  (service.provider?.service_types?.length > 1 && !selectedLocation) ||
                  // Block booking when the mobile travel quote says the address
                  // is outside the provider's radius. Defence-in-depth, the
                  // backend also re-validates on submit.
                  (selectedLocation === 'mobile' && travelQuote && travelQuote.within_radius === false)
                }
                className="w-full btn-primary h-14 text-base rounded-lg"
                data-testid="book-now-btn"
              >
                {bookingLoading
                  ? 'Processing...'
                  : service.provider?.is_tutorial
                    ? 'Bookings disabled, tutorial account'
                    : getBookButtonText(service, totalPrice)}
              </Button>
            </>
          )}

          {isProProvider && selectedDate && selectedTime && (
            <Button
              variant={selectedSlotStatus === 'booked' ? 'default' : 'outline'}
              onClick={handleJoinWaitlist}
              disabled={waitlistLoading || onWaitlist}
              className={`w-full gap-2 ${
                selectedSlotStatus === 'booked'
                  ? 'btn-primary h-14 text-base rounded-lg'
                  : 'mt-2 border-[#E6E4DD] text-stone-500 hover:text-[#D4AF37] hover:border-[#D4AF37]'
              }`}
              data-testid="join-waitlist-btn"
            >
              <Bell className="h-4 w-4" />
              {onWaitlist
                ? 'On the waitlist'
                : waitlistLoading
                  ? 'Joining...'
                  : selectedSlotStatus === 'booked'
                    ? `Join waitlist${waitlistCounts[selectedTime] ? ` (${waitlistCounts[selectedTime]} ahead)` : ''}`
                    : 'Slot taken? Join waitlist'}
            </Button>
          )}

          {!user && (
            <p className="text-center text-sm text-stone-500 mt-4">
              No account needed. Just enter your details at checkout
            </p>
          )}
        </div>
      </div>

      <GuestCheckoutDialog
        open={showGuestDialog}
        onOpenChange={setShowGuestDialog}
        service={service}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        totalPrice={totalPrice}
        guestName={guestName}
        setGuestName={setGuestName}
        guestEmail={guestEmail}
        setGuestEmail={setGuestEmail}
        guestPhone={guestPhone}
        setGuestPhone={setGuestPhone}
        bookingLoading={bookingLoading}
        onSubmit={handleGuestCheckout}
        serviceId={service.service_id}
      />

      <GuestWaitlistDialog
        open={showGuestWaitlistDialog}
        onOpenChange={setShowGuestWaitlistDialog}
        guestName={guestName}
        setGuestName={setGuestName}
        guestEmail={guestEmail}
        setGuestEmail={setGuestEmail}
        guestPhone={guestPhone}
        setGuestPhone={setGuestPhone}
        loading={waitlistLoading}
        onSubmit={() => submitWaitlist({
          name: guestName,
          email: guestEmail,
          phone: guestPhone,
          isGuest: true,
        })}
      />

      <CardOnFileDialog
        open={showCardDialog}
        onOpenChange={setShowCardDialog}
        bookingId={pendingBooking?.booking_id}
        maxCharge={pendingBooking?.max_fee || 0}
        guestToken={pendingBooking?.guest_token}
        onSuccess={() => {
          // Card saved, now proceed to payment checkout
          if (pendingBooking?.guest_token) {
            continueToGuestCheckoutAfterCard();
          } else {
            continueToCheckoutAfterCard();
          }
        }}
      />
    </>
  );
}

